/**
 * Subscription React Query Hooks
 * SESSION_185: Frontend Stripe Integration
 *
 * Provides React Query hooks for subscription management.
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { SubscriptionRepository } from '../../infrastructure/repositories/SubscriptionRepository';
import { apiClient } from '../../infrastructure/api/ApiClient';
import { notificationService } from '../../infrastructure/notifications';
import { queryKeys } from './queryKeys';
import { CACHE_CONFIG } from './queryClient';
import {
  SubscriptionDto,
  SubscriptionAccessInfo,
  CreateSubscriptionRequest,
  CreateSubscriptionResponse,
  CancelSubscriptionResponse,
  GetMySubscriptionResponse,
  GetSubscriptionAccessResponse
} from '../dto/subscription';

/**
 * Repository instance (singleton)
 */
const subscriptionRepository = new SubscriptionRepository(apiClient);

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch current user's subscription query hook.
 * GET /api/subscriptions/me
 *
 * @param enabled - Whether the query should run (default: true)
 * @returns The user's subscription details or null if no subscription
 */
export const useMySubscriptionQuery = (
  enabled: boolean = true
): UseQueryResult<GetMySubscriptionResponse, Error> => {
  return useQuery({
    queryKey: queryKeys.subscriptions.my(),
    queryFn: async (): Promise<GetMySubscriptionResponse> => {
      return subscriptionRepository.getMySubscription();
    },
    staleTime: CACHE_CONFIG.USER.staleTime,
    gcTime: CACHE_CONFIG.USER.gcTime,
    enabled
  });
};

/**
 * Fetch subscription access info query hook.
 * GET /api/subscriptions/access
 *
 * Used for feature gating to check if user/business has active access.
 *
 * @param businessId - Optional business ID to check access for
 * @param enabled - Whether the query should run (default: true)
 * @returns Subscription access information for feature gating
 */
export const useSubscriptionAccessQuery = (
  businessId?: string,
  enabled: boolean = true
): UseQueryResult<GetSubscriptionAccessResponse, Error> => {
  return useQuery({
    queryKey: queryKeys.subscriptions.access(businessId),
    queryFn: async (): Promise<GetSubscriptionAccessResponse> => {
      return subscriptionRepository.getSubscriptionAccess(businessId);
    },
    staleTime: CACHE_CONFIG.USER.staleTime,
    gcTime: CACHE_CONFIG.USER.gcTime,
    enabled
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create subscription mutation params.
 */
interface CreateSubscriptionParams {
  businessClaimRequestId: string;
  paymentMethodId?: string;
  promotionCode?: string;
}

/**
 * Create subscription mutation hook.
 * POST /api/subscriptions
 *
 * Creates a new subscription for an approved business claim.
 * Returns client secret if additional payment confirmation is needed.
 */
export const useCreateSubscriptionMutation = (): UseMutationResult<
  CreateSubscriptionResponse,
  Error,
  CreateSubscriptionParams,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateSubscriptionParams): Promise<CreateSubscriptionResponse> => {
      const request: CreateSubscriptionRequest = {
        businessClaimRequestId: params.businessClaimRequestId,
        paymentMethodId: params.paymentMethodId,
        promotionCode: params.promotionCode
      };
      return subscriptionRepository.createSubscription(request);
    },
    onSuccess: (data): void => {
      // Invalidate subscription queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all });

      if (data.success) {
        if (data.clientSecret) {
          // Payment confirmation needed - don't show success yet
          notificationService.showInfo(
            'Please complete payment to activate your subscription.',
            'Payment Required'
          );
        } else {
          notificationService.showSuccess(
            'Your subscription has been created successfully!',
            'Subscription Created'
          );
        }
      }
    },
    onError: (error: Error): void => {
      console.error('Create subscription failed:', error);
      notificationService.showError(
        'Failed to create subscription. Please try again.',
        'Subscription Error'
      );
    }
  });
};

/**
 * Cancel subscription mutation params.
 */
interface CancelSubscriptionParams {
  subscriptionId: string;
  immediate?: boolean;
}

/**
 * Cancel subscription mutation hook.
 * DELETE /api/subscriptions/{id}
 *
 * Cancels an existing subscription. By default, cancels at end of billing period.
 * Set immediate=true to cancel immediately.
 */
export const useCancelSubscriptionMutation = (): UseMutationResult<
  CancelSubscriptionResponse,
  Error,
  CancelSubscriptionParams,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CancelSubscriptionParams): Promise<CancelSubscriptionResponse> => {
      return subscriptionRepository.cancelSubscription(
        params.subscriptionId,
        params.immediate ?? false
      );
    },
    onSuccess: (data): void => {
      // Invalidate subscription queries to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all });

      if (data.success) {
        notificationService.showSuccess(
          data.message || 'Your subscription has been canceled.',
          'Subscription Canceled'
        );
      }
    },
    onError: (error: Error): void => {
      console.error('Cancel subscription failed:', error);
      notificationService.showError(
        'Failed to cancel subscription. Please try again.',
        'Cancellation Error'
      );
    }
  });
};

// ============================================================================
// Promo Code Validation
// ============================================================================

/**
 * Result from the validate-promo endpoint.
 */
export interface ValidatePromoCodeResult {
  success: boolean;
  isValid: boolean;
  discountDescription?: string;
  message?: string;
}

/**
 * Validate promotion code mutation hook.
 * POST /api/subscriptions/validate-promo
 *
 * Validates a human-readable promotion code against Stripe.
 */
export const useValidatePromoCodeMutation = (): UseMutationResult<
  ValidatePromoCodeResult,
  Error,
  string,
  unknown
> => {
  return useMutation({
    mutationFn: async (code: string): Promise<ValidatePromoCodeResult> => {
      return apiClient.post<ValidatePromoCodeResult>(
        '/api/subscriptions/validate-promo',
        { code }
      );
    }
  });
};

// ============================================================================
// Helper Hooks
// ============================================================================

/**
 * Hook to check if user has active subscription access.
 * SESSION_337: Changed to use useMySubscriptionQuery for consistency with billing page.
 * The /api/subscriptions/me endpoint reliably returns subscription data, while
 * /api/subscriptions/access was returning hasAccess=false for valid subscriptions.
 *
 * @param enabled - Whether to enable the query
 * @returns Object with hasAccess boolean and loading state
 */
export const useHasSubscriptionAccess = (
  enabled: boolean = true
): { hasAccess: boolean; isLoading: boolean; accessInfo: SubscriptionAccessInfo | null } => {
  // SESSION_337: Use the same query as billing page which works correctly
  const { data, isLoading } = useMySubscriptionQuery(enabled);

  // Derive access from subscription data - matches billing page logic
  const hasAccess = data?.subscription?.hasActiveAccess ?? false;

  // Build accessInfo from subscription for backwards compatibility
  const accessInfo: SubscriptionAccessInfo | null = data?.subscription
    ? {
        hasAccess: data.subscription.hasActiveAccess,
        status: data.subscription.status,
        expiresAt: data.subscription.currentPeriodEnd ?? null,
        daysRemainingInGrace: data.subscription.daysRemainingInGrace ?? null,
        businessCount: data.subscription.locationCount,
        stripeSubscriptionId: data.subscription.stripeSubscriptionId ?? null,
        monthlyAmount: data.subscription.monthlyAmount
      }
    : null;

  return {
    hasAccess,
    isLoading,
    accessInfo
  };
};

/**
 * Hook to get the current user's subscription if they have one.
 * Convenience wrapper around useMySubscriptionQuery.
 *
 * @param enabled - Whether to enable the query
 * @returns Object with subscription data and loading state
 */
export const useCurrentSubscription = (
  enabled: boolean = true
): { subscription: SubscriptionDto | null; hasSubscription: boolean; isLoading: boolean } => {
  const { data, isLoading } = useMySubscriptionQuery(enabled);

  return {
    subscription: data?.subscription ?? null,
    hasSubscription: data?.hasSubscription ?? false,
    isLoading
  };
};
